import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CalendarClock, Info, Loader2, Lock } from 'lucide-react';
import { apiGet } from '@/lib/api';
import { addChildProfile, CLASS_OPTIONS } from '@/lib/childProfiles';

// A managed profile needs no second email — that is the whole point for
// households where only the parent has one.
export function AddChildDialog({ open, onOpenChange, chargeNotice, canAdd, tierName, maxChildren, onAdded }) {
  const [boards, setBoards] = useState([]);
  const [form, setForm] = useState({ name: '', board: '', class_level: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ name: '', board: '', class_level: '' });
    apiGet('/onboarding/all-boards')
      .then(d => setBoards(d?.boards || []))
      .catch(() => setBoards([]));
  }, [open]);

  const submit = async () => {
    if (!form.name.trim() || !form.board || !form.class_level) {
      toast.message('Please fill in the name, board and class');
      return;
    }
    setSaving(true);
    try {
      const res = await addChildProfile(form);
      toast.success(`${res.profile.name} added — switch to them any time`);
      onAdded?.(res.profile);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || 'Could not add this child');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="add-child-dialog">
        <DialogTitle className="font-heading text-xl font-bold text-slate-900">Add a child</DialogTitle>
        <DialogDescription className="text-xs text-slate-500 font-body">
          No separate email needed — this profile lives under your login. Each child gets their own
          board, class, chapters and study plan.
        </DialogDescription>

        {!canAdd ? (
          <div className="mt-3 rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-3" data-testid="add-child-blocked">
            <p className="text-[12px] font-heading text-amber-900 flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" /> Your {tierName} plan covers {maxChildren} child{maxChildren === 1 ? '' : 'ren'}
            </p>
            <p className="text-[11px] font-body text-amber-800 mt-1">
              Upgrade to Premium to study with 2 children on one account, each with their own plan and progress.
            </p>
            <Button size="sm" className="mt-2 h-7 text-[11px] bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="add-child-upgrade" onClick={() => { window.location.href = '/subscription'; }}>
              See Premium
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-2.5 mt-3">
              <div>
                <label className="text-[10px] font-heading uppercase tracking-widest text-slate-400">Child's name</label>
                <Input data-testid="add-child-name" value={form.name} placeholder="e.g. Aarav"
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="h-9 mt-1 rounded-xl text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-heading uppercase tracking-widest text-slate-400">Board</label>
                  <select data-testid="add-child-board" value={form.board}
                    onChange={e => setForm(f => ({ ...f, board: e.target.value }))}
                    className="w-full h-9 mt-1 rounded-xl border border-slate-200 text-sm font-body px-2">
                    <option value="">Select</option>
                    {boards.map(b => (
                      <option key={b.code || b.name} value={b.code || b.name}>{b.short_name || b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-heading uppercase tracking-widest text-slate-400">Class</label>
                  <select data-testid="add-child-class" value={form.class_level}
                    onChange={e => setForm(f => ({ ...f, class_level: e.target.value }))}
                    className="w-full h-9 mt-1 rounded-xl border border-slate-200 text-sm font-body px-2">
                    <option value="">Select</option>
                    {CLASS_OPTIONS.map(c => <option key={c} value={c}>Class {c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Be explicit about money BEFORE they commit — no surprises. */}
            {chargeNotice && (
              <div className="mt-3 rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-3" data-testid="add-child-charge-notice">
                <p className="text-[12px] font-heading text-emerald-900 flex items-center gap-1.5">
                  {chargeNotice.free_until ? <CalendarClock className="w-3.5 h-3.5" /> : <Info className="w-3.5 h-3.5" />}
                  {chargeNotice.headline}
                </p>
                <p className="text-[11px] font-body text-emerald-800 mt-1 leading-relaxed">{chargeNotice.detail}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="add-child-cancel">Cancel</Button>
              <Button size="sm" disabled={saving} onClick={submit} data-testid="add-child-submit"
                className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Add child
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AddChildDialog;
